import { useCallback, useRef } from 'react'

import { createGlobalStyle } from 'styled-components'
import type { Swiper as SwiperType } from 'swiper'
import { Pagination } from 'swiper/modules'
import { Swiper } from 'swiper/react'

import { usePreviewContext } from 'context/previewContext'
import { useUserProfile } from 'store'
import colors from 'styles/colors'
import { type IFile } from 'types/interfaces/IFile'

import { type IProps } from './FilePostWrapper.types'
import { renderFileComponentFactory } from './utils'
import { PostAudio } from '../PostAudio/PostAudio'
import { Sounds } from '../Sounds/Sounds'

type PreviewLocation = 'home' | 'profile' | 'purchased' | 'watched' | 'liked' | 'playlist'

function inferPreviewLocationFromQuery(dataQuery: readonly any[]): PreviewLocation {
  const k = String(dataQuery?.[0] ?? '').toLowerCase()

  if (k === 'gethomefeed') return 'home'

  if (k === 'getfeed') return 'profile'

  if (k === 'getprofilefeed' || k.includes('profile')) return 'profile'
  if (k.includes('purchased')) return 'purchased'
  if (k.includes('watched')) return 'watched'
  if (k.includes('liked')) return 'liked'
  if (k.includes('playlist')) return 'playlist'

  return 'home'
}

export const FilePostWrapper = ({
  dataQuery,
  postData,
  postIndex,
  allFiles,
  updateCounter,
  setVideoCurrentTime,
  setIsPlaying,
  isPlaying = false,
  // preloadMedia = false,
  onOpenOptions,
  isInViewport = true,
  showPreviewVideo = false
}: IProps) => {
  const swiperRef = useRef(null)
  const previewData = usePreviewContext()
  const userData = useUserProfile()

  // this is the important part
  const ensurePreviewContextIsCorrect = useCallback(() => {
    const location = inferPreviewLocationFromQuery(dataQuery)

    const viewerId = userData?.id
    const creatorId = postData?.user_id

    // Home: viewer
    if (location === 'home') {
      if (!viewerId) return
      previewData.setInitialData(viewerId, '', location, undefined, dataQuery)
      return
    }

    // Profile and everything else: creator (fallback to viewer if missing)
    const uid = creatorId ?? viewerId
    if (!uid) return

    previewData.setInitialData(uid, '', location, undefined, dataQuery)
  }, [dataQuery, postData?.user_id, previewData, userData?.id])

  const handleFileClick = useCallback(
    (
      key: number,
      fileType: string
    ) => {
      const {
        minimized,
        updateSelectedPost,
        handleMinimize,
        addModal
      } = previewData

      ensurePreviewContextIsCorrect()

      if (minimized) {
        updateSelectedPost(
          postIndex,
          key,
          fileType,
          postData,
          dataQuery
        )

        handleMinimize(false)
        return
      }

      addModal(
        postIndex,
        key,
        fileType,
        postData,
        dataQuery
      )
    },
    [
      previewData,
      ensurePreviewContextIsCorrect,
      postIndex,
      postData,
      dataQuery
    ]
  )
  const carouselContent = allFiles
    .filter(
      (file: IFile) =>
        file.url.includes('/video') ||
        file.type === 'video' ||
        file.url.includes('/image') ||
        file.type === 'photo'
    )
    .map((file: IFile, key: number) =>
      renderFileComponentFactory({
        file,
        key,
        postUserId: postData.user_id,
        allFiles,
        handleFileClick,
        setVideoCurrentTime,
        setIsPlaying,
        onOpenOptions,
        isInViewport,
        showPreviewVideo
        
      })
    )

  const onSlideChange = (swiper: SwiperType) => {
    updateCounter(swiper.activeIndex)

    if (swiperRef?.current) {
      const slides = (swiperRef.current as HTMLDivElement).getElementsByTagName('video')
      for (let i = 0; i < slides.length; i++) slides[i].pause()
    }
  }

  return (
    <>
      {allFiles.length === 1 && allFiles[0].url.includes('/sounds') ? (
        <PostAudio
          url={allFiles[0].url}
          updateSelectedPost={() => handleFileClick(0, 'sound')}
        />
      ) : (
        <div className="post-tags-wrapper">
          <PaginationBullet />
          <Swiper
            spaceBetween={5}
            ref={swiperRef}
            slidesPerView="auto"
            className={`post__files__carousel ${
              isPlaying ? 'post__files__carousel--playing' : ''
            }`}
            longSwipesRatio={0.1}
            onSlideChange={onSlideChange}
            pagination={true}
            modules={[Pagination]}
          >
            {carouselContent}
          </Swiper>

          <Sounds postData={postData} />
        </div>
      )}
    </>
  )
}

const PaginationBullet = createGlobalStyle`
  .swiper-pagination > .swiper-pagination-bullet-active {
    background-color: ${colors.white};
  }
`
